import React, { useState } from "react";

export type SettingsTabId =
  | "profile"
  | "hours"
  | "language"
  | "ordering"
  | "channel"
  | "deliveryPolicy"
  | "enterpriseCapacity"
  | "printer"
  | "terminalsAndStations"
  | "cardProcessingFee"
  | "tip"
  | "payrollAndTips"
  | "downloadClient"
  | "subscription"
  | "paymentAccount"
  | "textMessaging";

interface SubmenuItem {
  id: SettingsTabId;
  label: string;
  permission?: string;
  ownerOnly?: boolean;
}

interface SubmenuSection {
  id: string;
  label: string;
  items: SubmenuItem[];
}

interface SettingsSubmenuProps {
  activeTab: SettingsTabId;
  onSelect: (tab: SettingsTabId) => void;
  permissions: Record<string, boolean>;
  FEATURE_NAMES: Record<string, string>;
  isOwnerAtStore: boolean;
}

const defaultOpenSections: Record<string, boolean> = {
  store: true,
  ordering: true,
  operations: true,
  account: true,
};

export function SettingsSubmenu({
  activeTab,
  onSelect,
  permissions,
  FEATURE_NAMES,
  isOwnerAtStore,
}: SettingsSubmenuProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(defaultOpenSections);

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const sections: SubmenuSection[] = [
    {
      id: "store",
      label: "Store",
      items: [
        { id: "profile", label: "Profile" },
        { id: "hours", label: "Hours" },
        { id: "language", label: "Language" },
      ],
    },
    {
      id: "ordering",
      label: "Ordering",
      items: [
        { id: "ordering", label: "Order Taking" },
        { id: "channel", label: "Channel", permission: FEATURE_NAMES.ChannelPriceOverride },
        { id: "deliveryPolicy", label: "Delivery" },
        { id: "enterpriseCapacity", label: "Enterprise Capacity" },
      ],
    },
    {
      id: "operations",
      label: "Operations",
      items: [
        { id: "terminalsAndStations", label: "Terminal & Station", permission: FEATURE_NAMES.MultipleStationsAndTerminals },
        { id: "cardProcessingFee", label: "Card fee" },
        { id: "printer", label: "Print" },
        { id: "textMessaging", label: "Text messaging" },
        { id: "tip", label: "Customer Tips", permission: FEATURE_NAMES.AdvancedTipSettings },
        { id: "payrollAndTips", label: "Payroll & Tips", ownerOnly: true },
        { id: "downloadClient", label: "Download Client" },
      ],
    },
    {
      id: "account",
      label: "Account",
      items: [
        { id: "subscription", label: "Subscription", ownerOnly: true },
        { id: "paymentAccount", label: "Payment Account", ownerOnly: true },
      ],
    },
  ];

  return (
    <nav
      className="w-full lg:w-[220px] lg:max-w-[220px] lg:shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-[#e5e7eb] bg-white overflow-y-auto lg:min-h-0"
      aria-label="Settings"
    >
      <div className="py-2.5 px-2.5">
        {sections.map((section, sectionIndex) => {
          const isOpen = openSections[section.id] ?? true;
          const visibleItems = section.items.filter(
            (item) =>
              (!item.permission || permissions[item.permission]) &&
              (!item.ownerOnly || isOwnerAtStore)
          );
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.id} className={`${sectionIndex > 0 ? "pt-5" : ""} mb-1 last:mb-0`}>
              <button
                type="button"
                className="flex w-full items-center justify-between py-1 px-2 text-left text-[13px] font-medium uppercase tracking-[0.04em] text-[#6b7280] hover:text-[#4b5563] hover:bg-black/[0.02] transition-colors duration-150 focus:outline-none focus:ring-0 rounded-[4px]"
                onClick={() => toggleSection(section.id)}
                aria-expanded={isOpen}
              >
                <span>{section.label}</span>
                <svg
                  className={`h-3 w-3 text-[#9ca3af] transition-transform duration-200 ease-out shrink-0 ${isOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="border-b border-[#e5e7eb] mt-1 mb-1.5" aria-hidden />
              <div
                className="grid transition-[grid-template-rows] duration-200 ease-out"
                style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
              >
                <div className="min-h-0 overflow-hidden">
                  <ul className="space-y-0.5 pb-1">
                    {visibleItems.map((item) => {
                      const isActive = activeTab === item.id;
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            className={`w-full text-left py-2 pr-3 pl-3 text-[13px] transition-colors duration-150 focus:outline-none focus:ring-0 ${
                              isActive
                                ? "font-semibold text-[#f97316] border-l-2 border-l-[#f97316]"
                                : "text-[#475569] font-medium hover:text-[#111827]"
                            }`}
                            onClick={() => onSelect(item.id)}
                          >
                            {item.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
